import BaseComponentModel from './base-component-model';
import IButton from '../../models/button/IButton';
import colorStyle from '../../styles/utils/variables.module.scss';
import IShouldReturnEntity from '../../api-clients/IShouldReturnEntity';
import ICtaCard from '../../models/cta-card-set/ICtaCard';
import { Entry } from 'contentful';
import { ApplicationSettings } from '../../models/settings';
import { ButtonStyleType } from '../../constants/button-style-type';
import { extractCloudinaryImage } from '../../utils/mapping-helpers/cloudinary.helper';
import { replaceAttrSpaceWithHyphen } from '../../utils/string-helpers/replace-attr-space-with-hyphen';
import { ExcludedEntitiesHelper } from '../../api-clients/page-template-clients/helpers/excluded-entities-helper';

export default class CtaCardSetComponentModel extends BaseComponentModel {
  public excludedEntitiesHelper = new ExcludedEntitiesHelper();
  public component = 'CardSet';
  cards: ICtaCard[] = [];
  appSettings: ApplicationSettings;
  id?: string;

  constructor(data: any, appSettings: ApplicationSettings) {
    super('ctaCardSet', appSettings);
    this.appSettings = appSettings;
    this.id = data.id;

    if (!this.isEntityValid(data)) return;

    this.cards = this.filterAndTransformCards(data.cards as Entry<any>[]);
  }

  private isEntityValid(data: any): boolean {
    const validation = this.shouldReturnEntity(data);
    if (validation.shouldReturn) return true;

    this.exclusionReasons.push(...validation.shouldNotReturnReasons);
    this.isValid = false;
    return false;
  }

  protected shouldReturnEntity(entry: Entry<any>): IShouldReturnEntity {
    return this.excludedEntitiesHelper.shouldComponentBeReturned(
      this.component,
      entry,
      ['cards'],
      ['cards'],
    );
  }

  protected shouldReturnChildEntities(entry: Entry<any>): IShouldReturnEntity {
    return this.excludedEntitiesHelper.shouldComponentBeReturned(
      this.component,
      entry,
      ['fields'],
      ['fields'],
    );
  }

  private filterAndTransformCards(cards: Entry<any>[]): ICtaCard[] {
    const includedCards: ICtaCard[] = [];
    for (const card of cards) {
      const validation = this.shouldReturnChildEntities(card);
      if (validation.shouldReturn) {
        includedCards.push(this.transformCard(card));
      } else {
        this.exclusionReasons.push(...validation.shouldNotReturnReasons);
      }
    }
    return includedCards;
  }

  private transformCard(card?: Entry<any>): ICtaCard {
    const cardFields = card?.fields;
    const button = this.createButton(cardFields?.button?.fields);

    return {
      id: card?.sys.id,
      imageTransform: cardFields?.imageTransform,
      image: extractCloudinaryImage(
        cardFields?.image?.fields?.image,
        cardFields?.image?.fields?.altText,
      ),
      title: cardFields?.title,
      copyText: cardFields?.copyText,
      button,
    };
  }

  private createButton(buttonFields: any): IButton | undefined {
    if (!buttonFields) return undefined;

    const { branding } = this.appSettings;
    const isOutline = buttonFields.buttonStyle === ButtonStyleType.Outline;

    return {
      buttonID: buttonFields.id || replaceAttrSpaceWithHyphen(buttonFields.buttonName),
      buttonURL: buttonFields.url,
      buttonAriaLabel: buttonFields.buttonName,
      buttonNewTab: buttonFields.target,
      copyColor: isOutline ? branding.primaryColor : colorStyle.white,
      backgroundColor: isOutline ? colorStyle.white : branding.primaryColor,
      outlineColor: branding.primaryColor,
      hoverBackgroundColor: branding.primaryHoverColor,
      activeBackgroundColor: branding.primaryActiveColor,
      children: buttonFields.text,
      buttonSize: buttonFields.buttonSize,
      buttonStyle: buttonFields.buttonStyle,
      buttonType: 'button',
      heapIdentifier: buttonFields.id || replaceAttrSpaceWithHyphen(buttonFields.buttonName),
    };
  }
}
